import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  LinearProgress,
  Tooltip,
  Paper,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  Cancel as CancelIcon,
  CalendarMonth as CalendarIcon,
  Person as PersonIcon,
  LocalHospital as HospitalIcon,
  FilterList as FilterIcon,
  MedicalServices as MedicalIcon,
} from '@mui/icons-material';
import { RootState, AppDispatch } from '../../store';
import { fetchAppointments, cancelAppointment } from '../../store/slices/appointmentSlice';
import { showSuccess, showError } from '../../store/slices/uiSlice';

/**
 * Appointment List Page
 * Premium appointment listing for Civilhealth Healthcare System.
 *
 * @author Civilhealth Development Team
 */

interface Appointment {
  id: number;
  bookingNumber: string;
  doctorId: number;
  doctorName: string;
  doctorSpecialization: string;
  doctorMobile?: string;
  officerId: number;
  officerName: string;
  officerMobile: string;
  officerDesignation: string;
  officerDepartment: string;
  officerAddress: string;
  officerEmail?: string;
  familyMemberId: number | null;
  familyMemberName: string | null;
  familyMemberRelationship: string | null;
  patientName: string;
  patientMobile: string;
  patientEmail?: string;
  patientAge: number | null;
  patientGender: string | null;
  appointmentDate: string;
  appointmentTime: string;
  startTime?: string;
  endTime: string;
  appointmentType: string;
  status: 'PENDING' | 'ACCEPTED' | 'IN_PROGRESS' | 'VISITED' | 'CLOSED' | 'CANCELLED' | 'REJECTED' | 'NO_SHOW' | 'RESCHEDULED' | 'CONFIRMED' | 'DOCTOR_ASSIGNED' | 'DOCTOR_ON_THE_WAY';
  statusDisplayName: string;
  priority: string;
  reasonForVisit: string;
  symptoms: string;
  chiefComplaints: string;
  notes: string;
  specialNotes: string;
  fullVisitAddress: string;
  visitAddressLine1: string;
  visitAddressLine2: string;
  visitCity: string;
  visitState: string;
  visitPincode: string;
  visitLandmark: string;
  cancellationReason: string;
  doctorArrivalTime: string | null;
  doctorDepartureTime: string | null;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'info' | 'default' => {
  switch (status) {
    case 'CONFIRMED':
      return 'success';
    case 'PENDING':
      return 'warning';
    case 'COMPLETED':
      return 'info';
    case 'CANCELLED':
      return 'error';
    case 'IN_PROGRESS':
      return 'info';
    default:
      return 'default';
  }
};

const getStatusLabel = (status: string): string => {
  switch (status) {
    case 'PENDING':
      return 'Pending';
    case 'CONFIRMED':
      return 'Confirmed';
    case 'IN_PROGRESS':
      return 'In Progress';
    case 'COMPLETED':
      return 'Completed';
    case 'CANCELLED':
      return 'Cancelled';
    case 'NO_SHOW':
      return 'No Show';
    default:
      return status;
  }
};

const AppointmentListPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  const { appointments, loading, error } = useSelector((state: RootState) => state.appointment);

  // Check if user is a doctor - doctors can view but not book
  const isDoctor = user?.role === 'GOVERNMENT_DOCTOR';

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    // Fetch appointments for current user (uses /appointments/my endpoint)
    dispatch(fetchAppointments() as any);
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      dispatch(showError(error));
    }
  }, [error, dispatch]);

  const handleCancelAppointment = async (appointmentId: number) => {
    if (window.confirm('Are you sure you want to cancel this appointment?')) {
      try {
        await dispatch(
          cancelAppointment({
            id: appointmentId,
            reason: 'Cancelled by user',
            cancelledBy: user?.username || '',
          }) as any
        );
        dispatch(showSuccess('Appointment cancelled successfully'));
      } catch (err) {
        dispatch(showError('Failed to cancel appointment'));
      }
    }
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Filter appointments based on search and status
  const filteredAppointments = (appointments || []).filter((appointment: Appointment) => {
    const matchesSearch =
      !searchTerm ||
      appointment.doctorName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      appointment.bookingNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      appointment.officerName?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = !statusFilter || appointment.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  // Paginated appointments
  const paginatedAppointments = filteredAppointments.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box sx={{ p: 2, flex: 1, overflow: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            {isDoctor ? 'My Appointments' : 'Appointments'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isDoctor ? 'View your scheduled appointments' : 'Manage your appointments'}
          </Typography>
        </Box>
        {!isDoctor && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/appointments/book')}
            sx={{
              background: 'linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%)',
              borderRadius: 2,
            }}
          >
            Book Appointment
          </Button>
        )}
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3, borderRadius: 2, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Search by doctor name, booking number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="PENDING">Pending</MenuItem>
                  <MenuItem value="CONFIRMED">Confirmed</MenuItem>
                  <MenuItem value="IN_PROGRESS">In Progress</MenuItem>
                  <MenuItem value="COMPLETED">Completed</MenuItem>
                  <MenuItem value="CANCELLED">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('');
                }}
              >
                Clear
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Appointments Table */}
      <Card sx={{ borderRadius: 2, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
        {loading && <LinearProgress />}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'transparent' }}>
                {isDoctor ? (
                  // Doctor View - Show Patient/Officer info
                  <>
                    <TableCell sx={{ fontWeight: 600 }}>Patient</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Officer</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Date & Time</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </>
                ) : (
                  // Officer/Admin View - Show Doctor info
                  <>
                    <TableCell sx={{ fontWeight: 600 }}>Booking #</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Doctor</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Date & Time</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedAppointments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <CalendarIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 1 }} />
                      <Typography variant="h6" color="text.secondary">
                        {isDoctor ? 'No appointments assigned' : 'No appointments found'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {isDoctor ? 'Your assigned appointments will appear here' : 'Book your first appointment to get started'}
                      </Typography>
                      {!isDoctor && (
                        <Button
                          variant="contained"
                          startIcon={<AddIcon />}
                          onClick={() => navigate('/appointments/book')}
                        >
                          Book Appointment
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedAppointments.map((appointment: Appointment) => (
                  <TableRow
                    key={appointment.id}
                    hover
                    sx={{ '&:hover': { bgcolor: 'transparent' } }}
                  >
                    {isDoctor ? (
                      // Doctor View - Show Patient/Officer info
                      <>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <PersonIcon sx={{ mr: 1, color: 'primary.main', fontSize: 20 }} />
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {appointment.familyMemberName || appointment.officerName || 'Patient'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {appointment.familyMemberId ? 'Family Member' : 'Officer'}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {appointment.officerName || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2">
                              {appointment.appointmentDate
                                ? new Date(appointment.appointmentDate).toLocaleDateString('en-IN', {
                                    weekday: 'short',
                                    day: 'numeric',
                                    month: 'short',
                                    year: 'numeric',
                                  })
                                : '-'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {appointment.startTime} - {appointment.endTime}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {appointment.appointmentType?.replace('_', ' ') || 'Home Visit'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={getStatusLabel(appointment.status)}
                            color={getStatusColor(appointment.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Tooltip title="View Details">
                              <IconButton
                                size="small"
                                onClick={() => navigate(`/appointments/${appointment.id}`)}
                              >
                                <ViewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {(appointment.status === 'PENDING' || appointment.status === 'DOCTOR_ASSIGNED') && (
                              <Tooltip title="Go to Workflow">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => navigate('/doctor/workflow')}
                                >
                                  <HospitalIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </>
                    ) : (
                      // Officer/Admin View - Show Doctor info
                      <>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {appointment.bookingNumber || `APT-${appointment.id}`}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <HospitalIcon sx={{ mr: 1, color: 'primary.main', fontSize: 20 }} />
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {appointment.doctorName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {appointment.doctorSpecialization}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2">
                              {appointment.appointmentDate
                                ? new Date(appointment.appointmentDate).toLocaleDateString('en-IN', {
                                    weekday: 'short',
                                    day: 'numeric',
                                    month: 'short',
                                    year: 'numeric',
                                  })
                                : '-'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {appointment.startTime} - {appointment.endTime}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {appointment.appointmentType?.replace('_', ' ') || 'Regular'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={getStatusLabel(appointment.status)}
                            color={getStatusColor(appointment.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Tooltip title="View Details">
                              <IconButton
                                size="small"
                                onClick={() => navigate(`/appointments/${appointment.id}`)}
                              >
                                <ViewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {(appointment.status === 'PENDING' || appointment.status === 'CONFIRMED') && (
                              <Tooltip title="Cancel Appointment">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleCancelAppointment(appointment.id)}
                                >
                                  <CancelIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredAppointments.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Card>
    </Box>
  );
};

export default AppointmentListPage;